/**
 * Context Helper Utility
 * Centralized functions to get current user context (company, branch, role)
 * from localStorage across the application.
 * 
 * Usage:
 *   import { getCurrentCompanyId, getCurrentBranchId, getCurrentContext } from '../util/contextHelper';
 *   const companyId = getCurrentCompanyId();
 */

/**
 * Get current company ID from user context
 * @returns {number|null} Company ID or null if not found
 */
export const getCurrentCompanyId = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    return userDetails?.user?.currentContext?.companyId || null;
  } catch (error) {
    console.error('Error getting company ID from context:', error);
    return null;
  }
};

/**
 * Get current company name from user context
 * @returns {string|null} Company name or null if not found
 */
export const getCurrentCompanyName = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    return userDetails?.user?.currentContext?.companyName || null;
  } catch (error) {
    console.error('Error getting company name from context:', error);
    return null;
  }
};

/**
 * Get current branch ID from user context
 * For ADMIN, STAFF, CASHIER roles: returns branchId from currentContext
 * For SUPERADMIN: returns null (can select any branch)
 * @returns {number|null} Branch ID or null if not found
 */
export const getCurrentBranchId = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    const roleCode = userDetails?.user?.activeRole?.roleCode;
    
    // For ADMIN, STAFF, CASHIER - return branch from context
    if (roleCode === 'ADMIN' || roleCode === 'STAFFS' || roleCode === 'CASHIER' || roleCode === 'SUPERADMIN' || roleCode === 'HEADOFFICE') {
      return userDetails?.user?.currentContext?.branchId || null;
    }
    
    // For SUPERADMIN - return null (they can select any branch)
    return null;
  } catch (error) {
    console.error('Error getting branch ID from context:', error);
    return null;
  }
};

/**
 * Get current branch name from user context
 * @returns {string|null} Branch name or null if not found
 */
export const getCurrentBranchName = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    return userDetails?.user?.currentContext?.branchName || null;
  } catch (error) {
    console.error('Error getting branch name from context:', error);
    return null;
  }
};

/**
 * Get current role code from user context
 * @returns {string|null} Role code (SUPERADMIN, ADMIN, STAFFS, CASHIER) or null
 */
export const getCurrentRoleCode = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    return userDetails?.user?.activeRole?.roleCode || null;
  } catch (error) {
    console.error('Error getting role code from context:', error);
    return null;
  }
};

/**
 * Get current role name from user context
 * @returns {string|null} Role name or null if not found
 */
export const getCurrentRoleName = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    return userDetails?.user?.activeRole?.roleName || null;
  } catch (error) {
    console.error('Error getting role name from context:', error);
    return null;
  }
};

/**
 * Get full current context object
 * @returns {object|null} Context object with companyId, branchId, companyName, branchName
 */
export const getCurrentContext = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    return userDetails?.user?.currentContext || null;
  } catch (error) {
    console.error('Error getting current context:', error);
    return null;
  }
};

/**
 * Get all available contexts for user
 * @returns {array} Array of available contexts
 */
export const getAvailableContexts = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    return userDetails?.user?.availableContexts || [];
  } catch (error) {
    console.error('Error getting available contexts:', error);
    return [];
  }
};

/**
 * Check if user is SUPERADMIN
 * @returns {boolean} True if SUPERADMIN, false otherwise
 */
export const isSuperAdmin = () => {
  return getCurrentRoleCode() === 'SUPERADMIN';
};

/**
 * Check if user is ADMIN
 * @returns {boolean} True if ADMIN, false otherwise
 */
export const isAdmin = () => {
  return getCurrentRoleCode() === 'ADMIN';
};

/**
 * Check if user is STAFFS
 * @returns {boolean} True if STAFFS, false otherwise
 */
export const isStaff = () => {
  return getCurrentRoleCode() === 'STAFFS';
};

/**
 * Check if user is CASHIER
 * @returns {boolean} True if CASHIER, false otherwise
 */
export const isCashier = () => {
  return getCurrentRoleCode() === 'CASHIER';
};

/**
 * Check if user is HEADOFFICE
 * @returns {boolean} True if HEADOFFICE, false otherwise
 */
export const isHeadOffice = () => {
  return getCurrentRoleCode() === 'HEADOFFICE';
};

/**
 * Check if add/edit buttons should be hidden based on role and module
 * For HEADOFFICE: hide all add/edit buttons EXCEPT for deposits and add interest
 * @param {string} module - The module name (e.g., 'deposits', 'addInterest', 'loans', 'users', etc.)
 * @returns {boolean} True if buttons should be hidden, false if they should be shown
 */
export const shouldHideAddEditButtons = (module = '') => {
  const roleCode = getCurrentRoleCode();
  
  // If not HEADOFFICE, show buttons (return false to not hide)
  if (roleCode !== 'HEADOFFICE') {
    return false;
  }
  
  // For HEADOFFICE, hide buttons except for specific modules
  const allowedModules = ['deposits', 'addInterest', 'depositInterest'];
  const moduleLower = module.toLowerCase();
  
  // If module is in allowed list, don't hide (return false)
  // Otherwise, hide (return true)
  return !allowedModules.includes(moduleLower);
};

/**
 * Check if branch field should be hidden
 * Branch field is hidden for ADMIN, STAFF, CASHIER roles
 * Branch field is shown for SUPERADMIN role
 * @returns {boolean} True if branch field should be hidden, false otherwise
 */
export const shouldHideBranchField = () => {
  const roleCode = getCurrentRoleCode();
  const branchId = getCurrentBranchId();
  if(branchId){
    return true;
  }
  // Show branch field only for SUPERADMIN. Hide for all other roles.
  return roleCode !== 'SUPERADMIN';
};

/**
 * Get context for API filters
 * Returns object with companyId and branchId for filtering API requests
 * @returns {object} { companyId, branchId }
 */
export const getContextFilters = () => {
  const companyId = getCurrentCompanyId();
  const branchId = getCurrentBranchId();
  
  return {
    companyId,
    branchId
  };
};

/**
 * Get user full name
 * @returns {string|null} User's full name or null
 */
export const getCurrentUserName = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    return userDetails?.user?.name || null;
  } catch (error) {
    console.error('Error getting user name:', error);
    return null;
  }
};

/**
 * Get user email
 * @returns {string|null} User's email or null
 */
export const getCurrentUserEmail = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    return userDetails?.user?.email || null;
  } catch (error) {
    console.error('Error getting user email:', error);
    return null;
  }
};

/**
 * Get user ID
 * @returns {number|null} User ID or null
 */
export const getCurrentUserId = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    return userDetails?.user?.id || null;
  } catch (error) {
    console.error('Error getting user ID:', error);
    return null;
  }
};

export default {
  getCurrentCompanyId,
  getCurrentCompanyName,
  getCurrentBranchId,
  getCurrentBranchName,
  getCurrentRoleCode,
  getCurrentRoleName,
  getCurrentContext,
  getAvailableContexts,
  isSuperAdmin,
  isAdmin,
  isStaff,
  isCashier,
  isHeadOffice,
  shouldHideAddEditButtons,
  shouldHideBranchField,
  getContextFilters,
  getCurrentUserName,
  getCurrentUserEmail,
  getCurrentUserId
};
